import { ReactElement } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { Stack } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AddButton } from '@ui/actions'
import { useSnackbar } from '@ui/snackbar'
import { useResultsInvalidation } from '@editorHooks'
import { getModel } from '@queries'
import { addFoundation } from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'

interface Props {
  onCreated?: (foundationGuid: string) => void
}

const AddFoundationForm = ({ onCreated }: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const { projectId }: { projectId?: string } = useParams()
  const invalidateResults = useResultsInvalidation()
  const queryClient = useQueryClient()

  const { mutate: createNewFoundationElement, isLoading } = useMutation(
    () => addFoundation.request(projectId as string),
    {
      onSuccess: async (model: PlanarModel) => {
        await queryClient.invalidateQueries(getModel.getKey(projectId))

        invalidateResults(projectId as string)

        const newFoundation = model.foundationElements.at(-1)
        if (newFoundation) {
          onCreated?.(newFoundation.guid)
        }

        enqueueSnackbar('Fundament erfolgreich hinzugefügt', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Hinzufügen des Fundaments'), {
          variant: 'error',
        })
      },
    },
  )

  return (
    <Stack direction="row" justifyContent="end" spacing={1}>
      <AddButton
        variant="contained"
        loading={isLoading}
        onClick={() => createNewFoundationElement()}
        fullWidth
        disabled={false}
        data-cy="btn-submit-create-new-foundation-element"
      >
        Neues Fundament anlegen
      </AddButton>
    </Stack>
  )
}

export default AddFoundationForm
